import React, { useState, useRef, useEffect } from 'react';
import {
  C,
  BFI2_ITEMS, AI_LIT_ITEMS, CFPB_ITEMS, FSES_ITEMS,
  OVERCONF_ITEMS, LOSS_AV_ITEMS, HUSTON_ITEMS,
  scoreBFI2, scoreHuston, scoreOverconfidence, scoreLossAversion,
} from '../data/constants';
import { NudgeLogo, ProgressBar, PrimaryBtn, SecondaryBtn } from './shared/UI';

const SECTIONS = [
  { id: 'bfi2',      title: 'Personality Assessment',        desc: 'Please rate how accurately each statement describes you.',                                               count: 60 },
  { id: 'literacy',  title: 'Financial Literacy',            desc: 'Choose the best answer for each financial knowledge question.',                                          count: 20 },
  { id: 'aiLit',     title: 'AI Literacy',                   desc: 'Rate your agreement with each statement about artificial intelligence.',                                 count: 12 },
  { id: 'wellbeing', title: 'Financial Well-Being',          desc: 'How well does each statement describe your financial situation?',                                        count: 10 },
  { id: 'fses',      title: 'Financial Self-Efficacy',       desc: 'How true is each statement for you?',                                                                   count: 6  },
  { id: 'overconf',  title: 'Financial Knowledge Estimates', desc: 'Provide your best estimate range — you should be 90% confident the true answer falls within your range.',count: 10 },
  { id: 'lossAv',    title: 'Financial Decisions',           desc: 'For each gamble below, choose to Accept or Reject.',                                                    count: 6  },
];

const LIKERT_5 = ['Disagree strongly', 'Disagree a little', 'Neutral', 'Agree a little', 'Agree strongly'];
const FSES_4   = ['Not at all true', 'Slightly true', 'Mostly true', 'Completely true'];

// ─── helpers ──────────────────────────────────────────────────────────────────
const emptyAnswers = () => ({ bfi2: [], literacy: [], aiLit: [], wellbeing: [], fses: [], overconf: [], lossAv: [] });

export default function BaselineSurvey({ onComplete }) {
  const [section, setSection] = useState(0);
  const [qIdx,    setQIdx]    = useState(0);

  // *** KEY FIX: store answers in a ref so handlers always get fresh data ***
  const aRef = useRef(emptyAnswers());
  // Mirror to state just to trigger re-renders for UI
  const [snap, setSnap] = useState(aRef.current);

  // Keep section/qIdx readable inside setTimeout callbacks
  const secRef  = useRef(0);
  const qRef    = useRef(0);
  useEffect(() => { secRef.current = section; }, [section]);
  useEffect(() => { qRef.current   = qIdx;    }, [qIdx]);

  const sec        = SECTIONS[section];
  const totalItems = SECTIONS.reduce((s, x) => s + x.count, 0);
  const totalDone  = SECTIONS.slice(0, section).reduce((s, x) => s + x.count, 0) + qIdx + 1;

  // ─── write answer + advance ───────────────────────────────────────────────
  const write = (key, idx, val, isLast) => {
    const arr      = [...(aRef.current[key] || [])];
    arr[idx]       = val;
    aRef.current   = { ...aRef.current, [key]: arr };
    setSnap({ ...aRef.current }); // trigger re-render

    if (isLast) {
      // finished this section → advance after brief pause
      setTimeout(() => goNextSection(), 350);
    } else {
      setTimeout(() => setQIdx(q => q + 1), 180);
    }
  };

  const goNextSection = () => {
    const cur = secRef.current;
    if (cur < SECTIONS.length - 1) {
      setSection(cur + 1);
      setQIdx(0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // final scoring
      const a = aRef.current;
      onComplete({
        bfi2:           scoreBFI2(a.bfi2),
        literacy:       scoreHuston(a.literacy),
        aiLiteracy:     (a.aiLit.reduce((s, v) => s + (v || 0), 0) / AI_LIT_ITEMS.length) * 20,
        overconfidence: scoreOverconfidence(a.overconf),
        lossAversion:   scoreLossAversion(a.lossAv),
        raw:            a,
      });
    }
  };

  // ─── per-section handlers ─────────────────────────────────────────────────
  const handleBFI2      = (val) => write('bfi2',      qIdx, val + 1, qIdx === BFI2_ITEMS.length    - 1);
  const handleLiteracy  = (val) => write('literacy',  qIdx, val,     qIdx === HUSTON_ITEMS.length  - 1);
  const handleAiLit     = (val) => write('aiLit',     qIdx, val + 1, qIdx === AI_LIT_ITEMS.length  - 1);
  const handleWellbeing = (val) => write('wellbeing', qIdx, val,     qIdx === CFPB_ITEMS.length    - 1);
  const handleFses      = (val) => write('fses',      qIdx, val,     qIdx === FSES_ITEMS.length    - 1);
  const handleLossAv    = (val) => write('lossAv',    qIdx, val,     qIdx === LOSS_AV_ITEMS.length - 1);

  const handleOverconf = (field, rawVal) => {
    const arr    = [...(aRef.current.overconf || [])];
    arr[qIdx]    = { ...(arr[qIdx] || {}), [field]: parseFloat(rawVal) || null };
    aRef.current = { ...aRef.current, overconf: arr };
    setSnap({ ...aRef.current });
  };

  const overconfCurrentOk = () => {
    const e = snap.overconf[qIdx];
    return e && e.low != null && e.high != null;
  };

  const handleOverconfNext = () => {
    const isLast = qIdx === OVERCONF_ITEMS.length - 1;
    if (isLast) goNextSection();
    else setQIdx(q => q + 1);
  };

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>

      {/* Sticky header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'rgba(250,251,252,0.96)',
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${C.border}`,
        padding: '12px 24px',
      }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <NudgeLogo size="sm" />
            <span style={{ fontSize: 13, color: C.muted }}>
              Baseline Survey — Section {section + 1} of {SECTIONS.length}
            </span>
          </div>
          <ProgressBar current={totalDone} total={totalItems} />
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '36px 24px 80px' }}>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, color: C.navy, fontWeight: 400, marginBottom: 6 }}>
          {sec.title}
        </h2>
        <p style={{ color: C.muted, fontSize: 14, marginBottom: 28 }}>{sec.desc}</p>

        <div style={{ marginBottom: 24 }}>
          <ProgressBar current={qIdx + 1} total={sec.count} label={`Question ${qIdx + 1} of ${sec.count}`} />
        </div>

        {/* ── BFI-2 ── */}
        {sec.id === 'bfi2' && (
          <QuestionCard
            prompt="I am someone who..."
            question={BFI2_ITEMS[qIdx]}
            options={LIKERT_5}
            selected={snap.bfi2[qIdx] != null ? snap.bfi2[qIdx] - 1 : null}
            onSelect={handleBFI2}
            onPrev={() => qIdx > 0 && setQIdx(q => q - 1)}
            onNext={() => qIdx < BFI2_ITEMS.length - 1 && setQIdx(q => q + 1)}
            canNext={snap.bfi2[qIdx] != null}
          />
        )}

        {/* ── Literacy ── */}
        {sec.id === 'literacy' && (
          <QuestionCard
            question={HUSTON_ITEMS[qIdx].q}
            options={HUSTON_ITEMS[qIdx].opts}
            selected={snap.literacy[qIdx] ?? null}
            onSelect={handleLiteracy}
            onPrev={() => qIdx > 0 && setQIdx(q => q - 1)}
            onNext={() => qIdx < HUSTON_ITEMS.length - 1 && setQIdx(q => q + 1)}
            canNext={snap.literacy[qIdx] != null}
          />
        )}

        {/* ── AI Literacy ── */}
        {sec.id === 'aiLit' && (
          <QuestionCard
            question={AI_LIT_ITEMS[qIdx]}
            options={LIKERT_5}
            selected={snap.aiLit[qIdx] != null ? snap.aiLit[qIdx] - 1 : null}
            onSelect={handleAiLit}
            onPrev={() => qIdx > 0 && setQIdx(q => q - 1)}
            onNext={() => qIdx < AI_LIT_ITEMS.length - 1 && setQIdx(q => q + 1)}
            canNext={snap.aiLit[qIdx] != null}
          />
        )}

        {/* ── Well-Being ── */}
        {sec.id === 'wellbeing' && (
          <QuestionCard
            question={CFPB_ITEMS[qIdx].q}
            options={CFPB_ITEMS[qIdx].scale}
            selected={snap.wellbeing[qIdx] ?? null}
            onSelect={handleWellbeing}
            onPrev={() => qIdx > 0 && setQIdx(q => q - 1)}
            onNext={() => qIdx < CFPB_ITEMS.length - 1 && setQIdx(q => q + 1)}
            canNext={snap.wellbeing[qIdx] != null}
          />
        )}

        {/* ── FSES ── */}
        {sec.id === 'fses' && (
          <QuestionCard
            question={FSES_ITEMS[qIdx]}
            options={FSES_4}
            selected={snap.fses[qIdx] ?? null}
            onSelect={handleFses}
            onPrev={() => qIdx > 0 && setQIdx(q => q - 1)}
            onNext={() => qIdx < FSES_ITEMS.length - 1 && setQIdx(q => q + 1)}
            canNext={snap.fses[qIdx] != null}
          />
        )}

        {/* ── Overconfidence (manual Next required) ── */}
        {sec.id === 'overconf' && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28 }}>
            <p style={{ fontSize: 16, color: C.text, fontWeight: 500, lineHeight: 1.6, marginBottom: 10 }}>
              {OVERCONF_ITEMS[qIdx].q}
            </p>
            <p style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
              Enter two numbers so you are 90% confident the real answer falls between them ({OVERCONF_ITEMS[qIdx].unit}).
            </p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {['low', 'high'].map(f => (
                <div key={f} style={{ flex: 1, minWidth: 140 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                    {f === 'low' ? 'Low Estimate' : 'High Estimate'}
                  </label>
                  <input
                    type="number"
                    value={snap.overconf[qIdx]?.[f] ?? ''}
                    onChange={e => handleOverconf(f, e.target.value)}
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: `1px solid ${C.border}`, borderRadius: 8,
                      fontSize: 15, color: C.text, outline: 'none',
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <SecondaryBtn onClick={() => qIdx > 0 && setQIdx(q => q - 1)} style={{ padding: '8px 16px', fontSize: 13 }}>
                Previous
              </SecondaryBtn>
              <PrimaryBtn onClick={handleOverconfNext} disabled={!overconfCurrentOk()} style={{ padding: '8px 22px', fontSize: 13 }}>
                {qIdx === sec.count - 1 ? 'Finish Section →' : 'Next'}
              </PrimaryBtn>
            </div>
          </div>
        )}

        {/* ── Loss Aversion ── */}
        {sec.id === 'lossAv' && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28 }}>
            <p style={{ fontSize: 14, color: C.muted, marginBottom: 24 }}>
              Evaluate this gamble and choose to Accept or Reject.
            </p>
            <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
              <div style={{ flex:1, background:'#dcfce7', border:'1px solid #a7f3d0', borderRadius:10, padding:'20px', textAlign:'center', minWidth:120 }}>
                <p style={{ fontSize:12, color:'#065f46', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>50% CHANCE TO WIN</p>
                <p style={{ fontFamily:"'DM Serif Display', serif", fontSize:32, color:'#059669', margin:0 }}>${LOSS_AV_ITEMS[qIdx].gain}</p>
              </div>
              <div style={{ flex:1, background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:10, padding:'20px', textAlign:'center', minWidth:120 }}>
                <p style={{ fontSize:12, color:'#991b1b', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>50% CHANCE TO LOSE</p>
                <p style={{ fontFamily:"'DM Serif Display', serif", fontSize:32, color:C.coral, margin:0 }}>${LOSS_AV_ITEMS[qIdx].loss}</p>
              </div>
            </div>
            <div style={{ display:'flex', gap:12 }}>
              {['accept','reject'].map(v => (
                <button key={v} onClick={() => handleLossAv(v)}
                  style={{
                    flex:1, padding:'13px',
                    border: snap.lossAv[qIdx] === v
                      ? `2px solid ${v === 'accept' ? C.teal : C.coral}`
                      : `1px solid ${C.border}`,
                    borderRadius:8,
                    background: snap.lossAv[qIdx] === v
                      ? (v === 'accept' ? C.tealLight : '#fee2e2') : '#fff',
                    color: snap.lossAv[qIdx] === v
                      ? (v === 'accept' ? C.teal : C.coral) : C.text,
                    fontWeight:600, fontSize:15, cursor:'pointer',
                    fontFamily:"'DM Sans', sans-serif", textTransform:'capitalize',
                  }}
                >{v}</button>
              ))}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-start', marginTop:20 }}>
              <SecondaryBtn onClick={() => qIdx > 0 && setQIdx(q => q - 1)} style={{ padding:'8px 16px', fontSize:13 }}>
                Previous
              </SecondaryBtn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shared question card (auto-advances on click) ────────────────────────────
function QuestionCard({ prompt, question, options, selected, onSelect, onPrev, onNext, canNext }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28 }}>
      {prompt && (
        <p style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
          {prompt}
        </p>
      )}
      <p style={{ fontSize: 16, color: C.text, fontWeight: 500, lineHeight: 1.6, marginBottom: 24 }}>{question}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {options.map((opt, i) => (
          <button
            key={i}
            onClick={() => onSelect(i)}
            className="select-btn"
            style={{
              padding: '11px 16px', borderRadius: 8,
              border:      selected === i ? `1.5px solid ${C.teal}` : `1px solid ${C.border}`,
              background:  selected === i ? C.tealLight : '#fff',
              color:       selected === i ? C.teal : C.text,
              fontWeight:  selected === i ? 600 : 400,
              fontSize: 14, cursor: 'pointer', textAlign: 'left',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {opt}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        <SecondaryBtn onClick={onPrev} style={{ padding: '8px 16px', fontSize: 13 }}>Previous</SecondaryBtn>
        <SecondaryBtn onClick={onNext} disabled={!canNext} style={{ padding: '8px 16px', fontSize: 13 }}>Next</SecondaryBtn>
      </div>
    </div>
  );
}
